import { useFlags } from '@atlaskit/flag';
import TextField from '@atlaskit/textfield';
import { IAddress } from 'core/api/address/address-api-interface';
import AddressApiService from 'core/api/address/address-api.service';
import { showErrorFlag } from 'core/utilities/flags-helper';
import { useState } from 'react';
import SharedButton from 'shared/components/buttons/button';

export interface IManualAddressFormOutput {
  [key: string]: string | undefined;
  addressLine1: string;
  addressLine2?: string;
  addressLine3?: string;
  city?: string;
  postcode: string;
}

interface IManualAddressForm {
  onChange?: (value?: { detail: IAddress; manualInput: boolean }) => void;
}

const ManualAddressForm = ({ onChange }: IManualAddressForm) => {
  const [submitting, setSubmitting] = useState(false);
  const [errorKeys, setErrorKeys] = useState<string[]>([]);
  const flags = useFlags();
  const [line1, setLine1] = useState('');
  const [line2, setLine2] = useState('');
  const [line3, setLine3] = useState('');
  const [city, setCity] = useState('');

  const [postcode, setPostcode] = useState('');

  const saveManualAddress = async () => {
    try {
      setSubmitting(true);
      let errors: string[] = [];
      if (line1 === '') {
        errors.push('addressLine1');
      }
      if (postcode === '') {
        errors.push('postcode');
      }
      if (errors.length > 0) {
        setErrorKeys(errors);
        setSubmitting(false);
        showErrorFlag('An error occurred', 'Please fill in all required fields.', flags);
        return;
      }
      const result = await AddressApiService.getFullAddressFromAddressFormOutputGoogle({
        addressLine1: line1,
        addressLine2: line2,
        addressLine3: line3,
        city,
        postcode,
      });
      if (!result) {
        throw new Error('No result from address lookup');
      }
      onChange?.({ detail: result, manualInput: true });
    } catch (error) {
      setSubmitting(false);
      showErrorFlag(
        'An error occurred',
        'We could not validate the address you entered, please check the details and try again.',
        flags,
      );
    }
  };

  const fields = [
    {
      fieldKey: 'addressLine1',
      placeholder: 'Address Line 1',
      required: true,
    },
    {
      fieldKey: 'addressLine2',
      placeholder: 'Address Line 2',
      required: false,
    },
    {
      fieldKey: 'addressLine3',
      placeholder: 'Address Line 3',
      required: false,
    },
    {
      fieldKey: 'city',
      placeholder: 'City',
      required: false,
    },
    {
      fieldKey: 'postcode',
      placeholder: 'Postcode',
      required: true,
    },
  ];

  const getFieldValue = (fieldKey: string) => {
    switch (fieldKey) {
      case 'addressLine1':
        return line1;
      case 'addressLine2':
        return line2;
      case 'addressLine3':
        return line3;
      case 'city':
        return city;
      case 'postcode':
        return postcode;
      default:
        return '';
    }
  };

  const onValueChange = (fieldKey: string, value: string) => {
    setErrorKeys([]);
    switch (fieldKey) {
      case 'addressLine1':
        setLine1(value);
        break;
      case 'addressLine2':
        setLine2(value);
        break;
      case 'addressLine3':
        setLine3(value);
        break;
      case 'city':
        setCity(value);
        break;
      case 'postcode':
        setPostcode(value);
        break;
      default:
        break;
    }
  };

  return (
    <div className='pb-4 mt-2 flex flex-col space-y-2'>
      {fields.map((field) => (
        <TextField
          value={getFieldValue(field.fieldKey)}
          key={field.fieldKey}
          placeholder={field.placeholder}
          isInvalid={errorKeys.includes(field.fieldKey)}
          onChange={(e) => onValueChange(field.fieldKey, e.currentTarget.value)}
        />
      ))}
      <SharedButton
        isLoading={submitting}
        onClick={saveManualAddress}
        type={undefined}
        appearance='primary'
        label='Save'
      />
    </div>
  );
};

export default ManualAddressForm;
